const FOCUSABLE_ELEMENT_SELECTORS = [
    'a[href]',
    '[href]',
    'area[href]',
    'input',
    'select',
    'textarea',
    'button',
    'iframe',
    'object',
    '[tabindex="0"]',
    '[contenteditable]',
    '.TriAutocomplete',
    '.TriButton',
    '.TriCheckbox',
    '.TriCurrencyInput',
    '.TriDatePicker',
    '.TriDateRangePicker',
    '.TriInput',
    '.TriLink',
    '.TriPhoneInput',
    '.TriRadioButton',
    '.TriSelect',
    '.TriSwitch',
    '.TriTextarea'
].join(', ');
// Deprecated
// Use either KEYS or CODES
const KEY_CODE = {
    TAB: 9,
    SHIFT: 16,
    ESC: 27,
    SPACE: 32,
    UPARROW: 38,
    DOWNARROW: 40,
    LEFTARROW: 37,
    RIGHTARROW: 39,
    ENTER: 13,
    CTRL: 17,
    ALT: 18
};
const CODE = {
    SHIFT_LEFT: 'ShiftLeft',
    SHIFT_RIGHT: 'ShiftRight',
    SPACE: 'Space',
    ESC: 'Escape',
    CTRL_LEFT: 'ControlLeft',
    CTRL_RIGHT: 'ControlRight',
    ALT_RIGHT: 'AltRight',
    ALT_LEFT: 'AltLeft',
    ENTER: 'Enter',
    TAB: 'Tab',
    ARROW_LEFT: 'ArrowLeft',
    ARROW_UP: 'ArrowUp',
    ARROW_RIGHT: 'ArrowRight',
    ARROW_DOWN: 'ArrowDown',
    PAGE_UP: 'Numpad9',
    PAGE_DOWN: 'Numpad3',
    END: 'Numpad1',
    HOME: 'Numpad7'
};
const KEY = {
    SHIFT_LEFT: 'Shift',
    SHIFT_RIGHT: 'Shift',
    SPACE: ' ',
    ESC: 'Escape',
    CTRL_LEFT: 'Control',
    CTRL_RIGHT: 'Control',
    ALT_RIGHT: 'Alt',
    ALT_LEFT: 'Alt',
    ENTER: 'Enter',
    TAB: 'Tab',
    ARROW_LEFT: 'ArrowLeft',
    ARROW_UP: 'ArrowUp',
    ARROW_RIGHT: 'ArrowRight',
    ARROW_DOWN: 'ArrowDown',
    PAGE_UP: 'PageUp',
    PAGE_DOWN: 'PageDown',
    END: 'End',
    HOME: 'Home'
};
const getKeyboardFocusableElements = (element = document)=>Array.from(element.querySelectorAll(FOCUSABLE_ELEMENT_SELECTORS)).filter((el)=>!el.hasAttribute('disabled') && el.getAttribute('aria-hidden') !== '');
const focusPrevElement = (element)=>{
    const focusables = getKeyboardFocusableElements();
    const i = focusables.findIndex((el)=>el === element);
    if (i === 0) focusables[focusables.length - 1].focus();
    else focusables[i - 1].focus();
};
const focusNextElement = (element)=>{
    const focusables = getKeyboardFocusableElements();
    const i = focusables.findIndex((el)=>el === element);
    if (i === focusables.length - 1) focusables[0].focus();
    else focusables[i + 1].focus();
};
const FocusManager = {
    FOCUSABLE_ELEMENT_SELECTORS,
    keymap: {
        CODE,
        KEY_CODE,
        KEY
    },
    getKeyboardFocusableElements,
    focusPrevElement,
    focusNextElement
};
Object.freeze(FocusManager);

/* eslint-disable no-param-reassign */ /**
 * All possible aria roles.
 * https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/ARIA_Techniques
 */ /**
 * Updates the tabindex and respects tab index set by the page author.
 * @param $elem
 * @param disabled
 */ function updateTabindex(element, disabled) {
    // eslint-disable-next-line no-nested-ternary
    element.tabIndex = disabled ? -1 : element.tabIndex < 0 ? 0 : element.tabIndex;
}

/**
 * Renders attributes to the given element based on the `attrInfo` object where
 * boolean values are added/removed as attributes.
 * @param {*} element Element on which to set attributes.
 * @param {*} attrMap Object describing attributes.
 */ function renderAttributes(element, attrMap) {
    Object.keys(attrMap).forEach((a)=>{
        const v = attrMap[a] === true ? '' : attrMap[a];
        if (v || v === '' || v === 0) {
            if (element.getAttribute(a) !== v) {
                element.setAttribute(a, v.toString());
            }
        } else if (element.hasAttribute(a)) {
            element.removeAttribute(a);
        }
    });
}

/* eslint-disable @typescript-eslint/no-explicit-any */ function emit(el, name, options) {
    const event = new CustomEvent(name, {
        bubbles: true,
        cancelable: false,
        composed: true,
        detail: {},
        ...options
    });
    el.dispatchEvent(event);
    return event;
}
function debounce(fn, timeout = 300) {
    let timer;
    return (...args)=>{
        if (timer) {
            window.clearTimeout(timer);
        }
        timer = window.setTimeout(()=>{
            fn.apply(this, args);
        }, timeout);
    };
}
function waitForEvent(el, eventName) {
    return new Promise((resolve)=>{
        function done(event) {
            if (event.target === el) {
                el.removeEventListener(eventName, done);
                resolve();
            }
        }
        el.addEventListener(eventName, done);
    });
}
function addEventListenerOnce(el, eventName, thisArg, eventListener) {
    const cb = (event)=>{
        el == null ? void 0 : el.removeEventListener(eventName, cb);
        eventListener.call(thisArg, event);
    };
    el.addEventListener(eventName, cb);
}

// Determines if the specified element is tabbable
function isTabbable(el) {
    const tag = el.tagName.toLowerCase();
    // Elements with a -1 tab index are not tabbable
    if (el.getAttribute('tabindex') === '-1') {
        return false;
    }
    // Elements with a disabled attribute are not tabbable
    if (el.hasAttribute('disabled')) {
        return false;
    }
    // Elements with aria-disabled are not tabbable
    if (el.hasAttribute('aria-disabled') && el.getAttribute('aria-disabled') !== 'false') {
        return false;
    }
    // Radios without a checked attribute are not tabbable
    if (tag === 'input' && el.getAttribute('type') === 'radio' && !el.hasAttribute('checked')) {
        return false;
    }
    // Elements that are hidden have no offsetParent and are not tabbable
    if (!el.offsetParent) {
        return false;
    }
    // Elements without visibility are not tabbable
    if (window.getComputedStyle(el).visibility === 'hidden') {
        return false;
    }
    // Audio and video elements with the controls attribute are tabbable
    if ((tag === 'audio' || tag === 'video') && el.hasAttribute('controls')) {
        return true;
    }
    // Elements with a tabindex other than -1 are tabbable
    if (el.hasAttribute('tabindex')) {
        return true;
    }
    // Elements with a contenteditable attribute are tabbable
    if (el.hasAttribute('contenteditable') && el.getAttribute('contenteditable') !== 'false') {
        return true;
    }
    // At this point, the following elements are considered tabbable
    return [
        'button',
        'input',
        'select',
        'textarea',
        'a',
        'audio',
        'video',
        'summary'
    ].includes(tag);
}
//
// Returns the first and last bounding elements that are tabbable. This is more performant than checking every single
// element because it short-circuits after finding the first and last ones.
//
function getTabbableBoundary(root, ignoreEle) {
    const allElements = [];
    function walk(el) {
        if (el instanceof HTMLElement && el !== ignoreEle) {
            allElements.push(el);
            if (el.shadowRoot && el.shadowRoot.mode === 'open') {
                walk(el.shadowRoot);
            }
        }
        [
            ...el.querySelectorAll('*')
        ].map((e)=>walk(e));
    }
    // Collect all elements including the root
    walk(root);
    // Find the first and last tabbable elements
    const start = allElements.find((el)=>isTabbable(el)) || null;
    const end = allElements.reverse().find((el)=>isTabbable(el)) || null;
    return {
        start,
        end
    };
}

let activeModals = [];
let Modal = class Modal {
    setOptions(options) {
        // Override existing options
        this.__options = {
            ...this.__options,
            ...options
        };
    }
    activate() {
        activeModals.push(this.element);
        document.addEventListener('focusin', this.handleFocusIn);
        document.addEventListener('keydown', this.handleKeyDown);
    }
    deactivate() {
        activeModals = activeModals.filter((modal)=>modal !== this.element);
        document.removeEventListener('focusin', this.handleFocusIn);
        document.removeEventListener('keydown', this.handleKeyDown);
    }
    isActive() {
        // The "active" modal is always the most recent one shown
        return activeModals[activeModals.length - 1] === this.element;
    }
    // This potentially gets called everytime focus changes within context of the drawer/modal
    // For elements that use dropdown/overlay
    handleFocusIn(event) {
        var _this___options;
        if ((_this___options = this.__options) == null ? void 0 : _this___options.disableFocusTrap) {
            // Do nothing if focus is not required to be constrained in the dialog.
            return;
        }
        /** Because of the way a dialog at the end of the document transfers focus,
      we support capturing focus by an optional endFocus element. */ const isEndOfDialog = function f(path, endFocus) {
            if (path[0] instanceof HTMLElement && path[0] === endFocus) {
                return true;
            }
            return false;
        };
        const path = event.composedPath();
        // If the focus has left our modal then bring it back inside the modal.
        if (this.isActive() && (!path.includes(this.element) && !this.pathIncludesOverlayItem(path) || isEndOfDialog(path, this.endFocus))) {
            const { start, end } = getTabbableBoundary(this.element, this.endFocus);
            // if we passed the first/last focusable ele of this modal, then "wrap"  the focus last/first
            let target = this.tabDirection === 'forward' ? start : end;
            if (target === null && this.endFocus) {
                target = this.endFocus;
            }
            // set focus to new target inside this modal
            if (typeof (target == null ? void 0 : target.focus) === 'function') {
                target.focus({
                    preventScroll: true
                });
            }
        }
    }
    handleKeyDown(event) {
        // Quick hack to determine tab direction
        if (event.key === 'Tab' && event.shiftKey) {
            this.tabDirection = 'backward';
            setTimeout(()=>{
                this.tabDirection = 'forward';
            });
        }
    }
    constructor(element, endFocus, options){
        this.tabDirection = 'forward';
        /**
   *  so the issue is that modal doesn't consider anything about overlay implementation
   *  (!path.includes(this.element) checks if the modal (drawer) contains the element with focus
   *  normally it does with your typical elements but overlay makes it weird since elements are hoisted into an overlay container
   *  only the host element of an overlay element is inside of the modal (drawer)
   */ // eslint-disable-next-line class-methods-use-this
        this.pathIncludesOverlayItem = (path)=>{
            let includesOverlayItem = false;
            path.forEach((el)=>{
                if (el.className === 'TriOverlayContainer') {
                    includesOverlayItem = true;
                }
            });
            return includesOverlayItem;
        };
        this.element = element;
        this.endFocus = endFocus;
        this.__options = options;
        this.handleFocusIn = this.handleFocusIn.bind(this);
        this.handleKeyDown = this.handleKeyDown.bind(this);
    }
};

class DisconnectedObserver {
    observe(node) {
        this.observeRemoval(node);
    }
    observeRemoval(node) {
        let temp = node;
        let parent = temp.parentNode;
        while(parent && parent !== document.body.parentNode){
            this.attachObserver(temp, parent);
            if (parent && parent.nodeType === 11) parent = parent.host;
            temp = parent;
            parent = temp.parentNode;
        }
    }
    attachObserver(child, parent) {
        const mutationObserver = new MutationObserver((mutationList)=>{
            for (const mutation of mutationList){
                const { removedNodes } = mutation;
                for (const node of removedNodes){
                    if (node === child) {
                        this.disconnectedCallback();
                        for (const observer of this.observers){
                            observer.disconnect();
                        }
                    }
                }
            }
        });
        mutationObserver.observe(parent, {
            subtree: false,
            childList: true
        });
        this.observers.push(mutationObserver);
    }
    constructor(disconnectCallback){
        this.observers = [];
        this.disconnectedCallback = disconnectCallback;
    }
}

/**
 * Traverses the roots and returns the first match.
 * @param $elem
 * @param query
 */ function queryParentRoots($elem, query) {
    if (!$elem.shadowRoot) {
        return [];
    }
    const $root = $elem.shadowRoot.host.getRootNode();
    const matches = $root.querySelectorAll(query);
    if (matches.length > 0) {
        return Array.from(matches);
    }
    return queryParentRoots($root, query);
}
/**
 * Finds ancestor node from a starting node. Returns undefined if no ancestor is found.
 */ const findAncestor = (// eslint-disable-next-line @typescript-eslint/no-explicit-any
node, // eslint-disable-next-line @typescript-eslint/no-explicit-any
searchFn)=>{
    let n = node;
    if (!n || n === window) {
        return undefined;
    }
    if (searchFn(n)) {
        return n;
    }
    if (n.assignedSlot) {
        n = n.assignedSlot;
    }
    if (n instanceof ShadowRoot) {
        return findAncestor(n.host, searchFn);
    }
    var _n_parentElement;
    return findAncestor((_n_parentElement = n.parentElement) != null ? _n_parentElement : n.parentNode, searchFn);
};
/**
 * Returns the first element that is a descendant of the element on which it is
 * invoked that matches the specified group of selectors. If no matches
 * are found, null is returned.
 * Note that selectors will not span across shadowRoots, so the following case
 * will not work with a selector of ".container .item":
 *
 * <div class="container">
 *   shadowRoot
 *     <div class="item"></div>
 * </div>
 *
 * Adapted from answer here: https://stackoverflow.com/questions/57813144/how-to-select-element-inside-open-shadow-dom-from-document
 */ const queryFromElementOrShadowRoot = (selectors, parentNode = document)=>{
    let targetNode = parentNode.querySelector(selectors);
    if (targetNode) {
        return targetNode;
    }
    const nodeIterator = document.createNodeIterator(parentNode, NodeFilter.SHOW_ELEMENT);
    let currentNode;
    // eslint-disable-next-line no-cond-assign
    while(currentNode = nodeIterator.nextNode()){
        if (currentNode === parentNode) {
            continue;
        }
        if (currentNode instanceof Element && currentNode.shadowRoot) {
            targetNode = queryFromElementOrShadowRoot(selectors, currentNode.shadowRoot);
        } else {
            targetNode = queryFromElementOrShadowRoot(selectors, currentNode);
        }
        if (targetNode) {
            return targetNode;
        }
    }
    return null;
};

// Copy from https://git.hq.bill.com/frontend/skylab/-/blob/master/packages/useNameFormatter/src/useMakeTestId/useMakeTestId.js
const kebabify = (strs)=>strs.filter(Boolean).join('-');
const createMakeTestId = (componentName, prefix)=>{
    const context = kebabify([
        prefix,
        componentName
    ]);
    return (dataTestId, parts = [])=>{
        if (Array.isArray(parts)) {
            return kebabify([
                context,
                dataTestId,
                ...parts
            ]);
        }
        return kebabify(Object.keys(parts).reduce((acc, key)=>parts[key] ? [
                ...acc,
                key
            ] : acc, [
            context,
            dataTestId
        ]));
    };
};

export { DisconnectedObserver, FocusManager, Modal, addEventListenerOnce, createMakeTestId, debounce, emit, findAncestor, getTabbableBoundary, queryFromElementOrShadowRoot, queryParentRoots, renderAttributes, updateTabindex, waitForEvent };
